import { isConfirmationRequired } from '../../services/authErrors'

/**
 * VH-285 — "enter your code" before a sensitive action (FinanceHub FH-104).
 *
 * The server refuses a sensitive action unless a code was confirmed on this
 * session within the last five minutes. `withConfirmation` turns that refusal
 * into a question: it opens the dialog, and when the code was confirmed it
 * runs the action once more.
 *
 *   await withConfirmation(() => profileAPI.changePassword(data))
 *
 * The dialog is rendered once, by <ConfirmCodeHost /> in the layout, which
 * registers itself here.
 */
let host = null

/** Called by the mounted dialog host. Returns the way to take it away again. */
export function registerConfirmHost(ask) {
  host = ask
  return () => {
    if (host === ask) host = null
  }
}

/** Opens the dialog. Resolves true when a code was confirmed, false when not. */
export function askForCode() {
  if (!host) return Promise.resolve(false)
  return new Promise((resolve) => host(resolve))
}

/** The action was not done because the reader closed the dialog. */
export const wasCancelled = (error) => Boolean(error?.confirmationCancelled)

export async function withConfirmation(action) {
  try {
    return await action()
  } catch (error) {
    if (!isConfirmationRequired(error)) throw error
    const confirmed = await askForCode()
    if (!confirmed) {
      throw Object.assign(new Error('The code was not confirmed'), { confirmationCancelled: true })
    }
    // Once. A second refusal goes to the caller as the failure it is.
    return action()
  }
}
